import React, { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Upload, AlertCircle, CheckCircle, Loader2, Brain } from 'lucide-react';
import { DEMO_MANDATE_ID, DEMO_ORGANIZATION_ID } from '@/hooks/use-active-mandate';

interface ProcessingResult {
  success: boolean;
  processedCount?: number;
  skippedDuplicates?: number;
  averageConfidence?: number;
  automationRate?: number;
  processingTimeMs?: number;
  highConfidenceCount?: number;
  uploadId?: string;
  errors: string[];
  message: string;
}

interface UploadFunctionResponse {
  success: boolean;
  uploadId?: string;
  message?: string;
  processed?: number;
  averageConfidence?: number;
  highConfidenceCount?: number;
  automationRate?: number;
  processingTimeMs?: number;
  error?: string;
}

interface FileUploadZoneProps {
  organizationId: string;
  mandateId: string;
  onUploadComplete?: () => void;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({ organizationId, mandateId, onUploadComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [processingResults, setProcessingResults] = useState<ProcessingResult[]>([]);

  const formatConfidencePercent = (value?: number) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 'N/A';
    }

    const normalized = value <= 1 ? value * 100 : value;
    return `${normalized.toFixed(1)}%`;
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setSelectedFiles(files);
      processFiles(files);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFiles(files);
      processFiles(files);
    }
  };

  const processFiles = async (filesToProcess: FileList) => {
    setIsProcessing(true);
    setProcessingResults([]);
    
    try {
      const filesArray = Array.from(filesToProcess);

      if (!mandateId || !organizationId) {
        let errorMessage = 'Setup incomplete: ';
        if (!organizationId) {
          errorMessage += 'Organization missing. ';
        }
        if (!mandateId) {
          errorMessage += 'Mandate missing. ';
        }
        errorMessage += 'Please complete the onboarding process first at /onboarding';
        
        const result: ProcessingResult = {
          success: false,
          errors: [errorMessage],
          message: errorMessage
        };
        setProcessingResults(prev => [...prev, result]);
        return;
      }

      const targetMandateId = mandateId;
      const targetOrganizationId = organizationId;

      for (const file of filesArray) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mandateId', targetMandateId);
        formData.append('organizationId', targetOrganizationId);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Keine aktive Sitzung. Bitte melden Sie sich erneut an.');
        }

        let payload: UploadFunctionResponse | null = null;
        try {
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/file-upload`, {
            method: 'POST',
            body: formData,
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          });

          payload = await response.json();

          if (!response.ok || payload?.success === false) {
            const errorMessage = payload?.error || payload?.message || `Upload failed (${response.status})`;
            const result: ProcessingResult = {
              success: false,
              errors: [errorMessage],
              message: errorMessage,
            };
            setProcessingResults(prev => [...prev, result]);
            continue;
          }
        } catch (requestError) {
          const errorMessage = requestError instanceof Error ? requestError.message : 'Upload failed';
          const result: ProcessingResult = {
            success: false,
            errors: [errorMessage],
            message: errorMessage,
          };
          setProcessingResults(prev => [...prev, result]);
          continue;
        }

        const processedCount = typeof payload?.processed === 'number' ? payload.processed : 0;
        const avgConfidence = typeof payload?.averageConfidence === 'number' ? payload.averageConfidence : 0;
        const automationRate = typeof payload?.automationRate === 'number' ? payload.automationRate : null;
        const highConfidenceCount = typeof payload?.highConfidenceCount === 'number' ? payload.highConfidenceCount : null;
        const processingTimeMs = typeof payload?.processingTimeMs === 'number' ? payload.processingTimeMs : null;
        const result: ProcessingResult = {
          success: true,
          processedCount,
          averageConfidence: avgConfidence,
          highConfidenceCount,
          automationRate,
          processingTimeMs,
          uploadId: typeof payload?.uploadId === 'string' ? payload.uploadId : undefined,
          errors: [],
          message: payload?.message || 'File uploaded. AI classification has been queued.'
        };
        setProcessingResults(prev => [...prev, result]);
        if (result.success) {
          onUploadComplete?.();
        }
      }
    } catch (error) {
      // Error handling for file processing
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-brand-border">
      <div
        className={`
          relative block w-full h-64 border-2 border-dashed rounded-xl text-center flex flex-col justify-center items-center cursor-pointer transition-all duration-300
          ${isDragging
            ? 'border-blue-500 bg-blue-50'
            : isProcessing
            ? 'border-green-500 bg-green-50'
            : 'border-brand-border hover:border-brand-primary bg-brand-secondary/30'
          }
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isProcessing ? (
          <>
            <div className="flex items-center justify-center mb-4">
              <Brain className="h-12 w-12 text-blue-600 animate-pulse mr-3" />
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            <h3 className="text-lg font-bold text-blue-600">Swiss AI Processing...</h3>
            <p className="text-sm text-blue-500 mt-1">
              Swiss AI Classification with Claude Sonnet 4.5
            </p>
            <div className="mt-4 w-full max-w-xs bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
            </div>
          </>
        ) : (
          <>
            <i className="fa-solid fa-cloud-arrow-up text-5xl text-brand-primary mb-4"></i>
            <h3 className="text-lg font-bold text-brand-primary">Drag & Drop Your Files Here</h3>
            <p className="text-sm text-brand-text-secondary mt-1">or click to browse your computer</p>
          </>
        )}

        <input
          type="file"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          multiple
          accept=".csv,.xml,.txt"
          onChange={handleFileChange}
          disabled={isProcessing}
        />
      </div>

      {/* Processing Results */}
      {processingResults.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-lg font-semibold text-brand-primary flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Swiss AI Processing Results
          </h4>
          {processingResults.map((result, index) => (
            <div key={index} className={`
              p-4 rounded-lg border-l-4
              ${result.success
                ? 'bg-green-50 border-green-500'
                : 'bg-red-50 border-red-500'
              }
            `}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className={`flex items-center space-x-2 ${
                    result.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {result.success ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <AlertCircle className="h-5 w-5" />
                    )}
                    <span className="font-medium">{result.message}</span>
                  </div>
                  {result.success && (
                    <div className="mt-2 space-y-2 text-sm">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <span className="font-medium text-green-700">Confidence:</span>
                          <span className="ml-2 text-green-600">{formatConfidencePercent(result.averageConfidence)}</span>
                        </div>
                        <div>
                          <span className="font-medium text-green-700">Processed:</span>
                          <span className="ml-2 text-green-600">{result.processedCount}</span>
                        </div>
                        <div>
                          <span className="font-medium text-green-700">Automation:</span>
                          <span className="ml-2 text-green-600">{result.automationRate ? `${result.automationRate.toFixed(1)}%` : 'N/A'}</span>
                        </div>
                      </div>
                      {result.skippedDuplicates !== undefined && result.skippedDuplicates > 0 && (
                        <div className="text-orange-600">
                          <span className="font-medium">Duplicates skipped:</span>
                          <span className="ml-2">{result.skippedDuplicates}</span>
                        </div>
                      )}
                      {result.processingTimeMs && (
                        <div className="text-gray-600">
                          <span className="font-medium">Processing time:</span>
                          <span className="ml-2">{(result.processingTimeMs / 1000).toFixed(1)}s</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <p className="text-xs text-center text-brand-text-secondary font-medium mb-2">
          Swiss AI-Powered Format Recognition & Classification:
        </p>
        <div className="flex justify-center items-center gap-4">
          <img
            className="h-6 object-contain"
            src="https://storage.googleapis.com/uxpilot-auth.appspot.com/4669050f6d-191bb88c8fc4051706b5.png"
            alt="UBS bank logo"
          />
          <img
            className="h-6 object-contain"
            src="https://storage.googleapis.com/uxpilot-auth.appspot.com/894ee70486-d4e5c0587f4b149bb5e6.png"
            alt="PostFinance bank logo"
          />
          <img
            className="h-6 object-contain"
            src="https://storage.googleapis.com/uxpilot-auth.appspot.com/7fa570a092-4db21184639775b47343.png"
            alt="TWINT logo"
          />
          <span className="text-sm font-bold text-brand-text-secondary">CAMT.053</span>
          <div className="flex items-center gap-1 text-blue-600">
            <Brain className="h-4 w-4" />
            <span className="text-xs font-bold">Claude 4.5</span>
          </div>
        </div>
        <div className="text-center mt-2">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            Swiss AI • 50 req/min • Claude Sonnet 4.5
          </span>
        </div>
      </div>
    </div>
  );
};
